/**
 * Massachusetts room occupancy (Form ROC) response shapes, mirroring
 * admin-api's `src/routes/admin-ma-tax.ts` (CAP-545, CAP-546, CAP-547).
 *
 * Local rather than in `@natalan/capehost-contracts` for now, like the cleaning
 * settings shapes: they are new and admin-api validates them inline too.
 * Moving both sides onto one published schema is the follow-up for the next
 * contracts release that touches this area.
 */
import { z } from 'zod'

export const MaTaxRegistrationSchema = z.object({
  property_id: z.string(),
  jurisdiction: z.enum(['ma', 'not_ma']),
  certificate_id: z.string().nullable(),
  operator_name: z.string().nullable(),
  street: z.string().nullable(),
  city: z.string().nullable(),
  zip: z.string().nullable(),
  municipality_code: z.string().nullable(),
  community_impact_fee_applies: z.boolean().nullable(),
  updated_at: z.string().optional(),
  updated_by: z.string().nullable().optional(),
})
export type MaTaxRegistration = z.infer<typeof MaTaxRegistrationSchema>

/** Rates are integer basis points: 570 = 5.70%. */
export const ResolvedRatesSchema = z.object({
  municipality_code: z.string(),
  municipality_name: z.string(),
  date: z.string(),
  state: z.number(),
  local: z.number(),
  water_protection: z.number(),
  convention_center: z.number(),
  community_impact_adopted: z.number(),
  tourism: z.number(),
})
export type ResolvedRates = z.infer<typeof ResolvedRatesSchema>

export const MaTaxRegistrationResponseSchema = z.object({
  registration: MaTaxRegistrationSchema.nullable(),
  problems: z.array(z.string()),
  rates_today: ResolvedRatesSchema.nullable(),
})
export type MaTaxRegistrationResponse = z.infer<typeof MaTaxRegistrationResponseSchema>

export const MaTaxMunicipalitiesResponseSchema = z.object({
  as_of: z.string(),
  municipalities: z.array(
    z.object({
      code: z.string(),
      name: z.string(),
      rates_today: ResolvedRatesSchema.nullable(),
    })
  ),
})

export const RocProblemSchema = z.object({
  code: z.string(),
  severity: z.enum(['error', 'review', 'info']),
  message: z.string(),
  reservation_id: z.string().optional(),
  ack_key: z.string().optional(),
  acknowledged: z.boolean().optional(),
  acknowledgment_note: z.string().optional(),
})
export type RocProblem = z.infer<typeof RocProblemSchema>

export const RocReservationLineSchema = z.object({
  reservation_id: z.string(),
  channel: z.string(),
  check_in: z.string().nullable(),
  check_out: z.string(),
  nights: z.number().nullable(),
  included: z.boolean(),
  exclusion_reason: z.string().nullable(),
  accommodation: z.number().nullable(),
  cleaning_fee: z.number().nullable(),
  extra_charges: z.number().nullable(),
  discounts: z.number().nullable(),
  rent: z.number().nullable(),
  taxable_rent: z.number().nullable(),
  exempt: z.boolean(),
  collected_tax: z.number().nullable(),
})
export type RocReservationLine = z.infer<typeof RocReservationLineSchema>

export const RocRowSchema = z.object({
  property_id: z.string(),
  property_name: z.string(),
  archived: z.boolean(),
  selected: z.boolean(),
  certificate_id: z.string().nullable(),
  operator_name: z.string().nullable(),
  street: z.string().nullable(),
  city: z.string().nullable(),
  zip: z.string().nullable(),
  municipality_code: z.string().nullable(),
  municipality_name: z.string().nullable(),
  rates_date: z.string(),
  total_rents: z.number(),
  taxable_rents: z.number(),
  state_tax_rate_bp: z.number(),
  state_tax: z.number(),
  local_tax_rate_bp: z.number(),
  local_option_tax: z.number(),
  convention_center_rate_bp: z.number(),
  convention_center_fund: z.number(),
  water_protection_rate_bp: z.number(),
  water_protection_fund: z.number(),
  community_impact_rate_bp: z.number(),
  community_impact_due: z.number(),
  tourism_fee_rate_bp: z.number(),
  tourism_fee: z.number(),
  total_tax: z.number(),
  collected_tax: z.number(),
  collected_difference: z.number(),
  reservation_ids: z.array(z.string()),
  reservations: z.array(RocReservationLineSchema),
  excluded_reservations: z.array(RocReservationLineSchema),
  problems: z.array(RocProblemSchema),
  status: z.enum(['ready', 'needs_review', 'error']),
})
export type RocRow = z.infer<typeof RocRowSchema>

export const RocTotalsSchema = z.object({
  properties: z.number(),
  reservations: z.number(),
  total_rents: z.number(),
  taxable_rents: z.number(),
  state_tax: z.number(),
  local_option_tax: z.number(),
  convention_center_fund: z.number(),
  water_protection_fund: z.number(),
  community_impact_due: z.number(),
  tourism_fee: z.number(),
  total_tax: z.number(),
  collected_tax: z.number(),
})

export const RocReportSchema = z.object({
  period: z.string(),
  period_start: z.string(),
  period_end: z.string(),
  rows: z.array(RocRowSchema),
  properties_without_stays: z.array(z.object({ property_id: z.string(), name: z.string() })),
  totals: RocTotalsSchema,
  excluded_counts: z.record(z.string(), z.number()),
  can_generate: z.boolean(),
  blocking_reasons: z.array(z.string()),
})
export type RocReport = z.infer<typeof RocReportSchema>

export const RocVersionSchema = z.object({
  id: z.string(),
  period: z.string(),
  version: z.number(),
  created_at: z.string(),
  created_by: z.string().nullable(),
  xlsx_sha256: z.string(),
  xlsx_bytes: z.number(),
  row_count: z.number(),
  reservation_count: z.number(),
  taxable_rents_cents: z.number(),
  total_tax_cents: z.number(),
  property_ids: z.array(z.string()),
  excluded_property_ids: z.array(z.string()),
  acknowledgments: z.record(z.string(), z.string()),
  is_amendment: z.boolean(),
  submitted_at: z.string().nullable(),
  submitted_by: z.string().nullable(),
  confirmation_number: z.string().nullable(),
  is_latest: z.boolean().optional(),
  filename: z.string().optional(),
})
export type RocVersion = z.infer<typeof RocVersionSchema>

export const RocVersionsResponseSchema = z.object({
  versions: z.array(RocVersionSchema),
  count: z.number(),
})

export const CreateRocVersionResponseSchema = z.object({
  version: RocVersionSchema,
  report: RocReportSchema,
})

export const MaTaxConfirmationSchema = z.object({
  confirmation_number: z.string(),
  period: z.string(),
  filing_period_raw: z.string(),
  account_id: z.string(),
  legal_name: z.string(),
  submitted_at_raw: z.string(),
  received_at: z.string(),
  status: z.enum(['matched', 'unmatched', 'ignored']),
  version_id: z.string().nullable(),
  note: z.string().nullable(),
})
export type MaTaxConfirmation = z.infer<typeof MaTaxConfirmationSchema>

export const MaTaxConfirmationsResponseSchema = z.object({
  confirmations: z.array(MaTaxConfirmationSchema),
  count: z.number(),
})

export const OwnerEmailPreviewSchema = z.object({
  property_id: z.string(),
  property_name: z.string(),
  recipients: z.array(z.object({ email: z.string(), name: z.string().nullable() })),
  subject: z.string(),
  html: z.string(),
  text: z.string(),
  sent: z
    .object({
      status: z.enum(['sending', 'sent', 'failed']),
      sent_at: z.string(),
      sent_by: z.string().nullable(),
      error: z.string().nullable(),
      recipients: z.array(z.string()),
    })
    .nullable(),
  problems: z.array(z.string()),
})
export type OwnerEmailPreview = z.infer<typeof OwnerEmailPreviewSchema>

export const OwnerEmailPreviewsResponseSchema = z.object({
  ready: z.boolean(),
  reason: z.string().optional(),
  bcc: z.string().optional(),
  confirmation: MaTaxConfirmationSchema.optional(),
  previews: z.array(OwnerEmailPreviewSchema),
})

export const SendOwnerEmailsResponseSchema = z.object({
  outcomes: z.array(
    z.object({
      property_id: z.string(),
      status: z.enum(['sent', 'failed', 'skipped']),
      reason: z.string().optional(),
    })
  ),
})
